import React, { Component } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { QueryResult, Query } from 'react-apollo';
import { themes } from 'kb-shared';

import Button from '../../Button';
import Result from './Result';
import { DashboardWidget } from '../../Card';

const ResultsQuery = gql`
  query resultsWidget {
    results {
      afc: follicleCounts {
        type
        name
        date
        units
        value
      }
      labs {
        type
        name
        date
        units
        value
      }
    }
  }
`;

type ResultType = {
  type: 'afc' | 'amh' | 'progesterone' | string;
  name: string;
  date: string;
  units: string;
  value: string;
};

type LoadedResults = {
  afc: Array<ResultType>;
  labs: Array<ResultType>;
};

type ResultsQueryProps = QueryResult<{
  results: LoadedResults;
}>;

type DerivedState =
  | { type: 'AllResults'; amh: ResultType; afc: ResultType } // All results are available
  | { type: 'EmptyAMH' } // Results are pending
  | { type: 'AMHProspect'; amh: ResultType } // AMH results are in, but no afc
  | { type: 'none' }; // Nothing is available, card will not render

type Props = {
  onViewResults: () => void;
  hasScheduledAMH: boolean;
};

type State = {
  step: string;
  showCommentModal: boolean;
};

export class ResultsWidget extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      step: 'start',
      showCommentModal: false
    };
  }

  getButtonProps(state: DerivedState): { title: string; action: () => void } | null {
    switch (state.type) {
      // These have changed time to time so leaving in the switch statement.
      case 'AllResults':
      case 'AMHProspect':
        return { title: 'Learn more', action: this.props.onViewResults };
      case 'EmptyAMH':
      default:
        return null;
    }
  }

  renderCommentModal() {
    //TODO: Implement once we have data for this.
    return null;
  }

  getDerivedState(results: LoadedResults): DerivedState {
    const { hasScheduledAMH } = this.props;
    const amh: ResultType | null = results.labs.filter(result => result.type === 'amh')[0];

    // If no date is present on amh them no results exist for it.
    if (amh && results.afc.length > 0) {
      //TODO Actually get latest by date sorting
      const latestAFC = results.afc[0];
      return { type: 'AllResults', amh: amh, afc: latestAFC };
    } else if (hasScheduledAMH && !amh) {
      return { type: 'EmptyAMH' };
    } else if (amh && results.afc.length === 0) {
      return { type: 'AMHProspect', amh: amh };
    }
    return { type: 'none' };
  }

  getTitleAttributes(state: DerivedState): { title: string; description: string } {
    switch (state.type) {
      case 'EmptyAMH':
      case 'AMHProspect':
        return {
          title: 'Your AMH Report:',
          description: 'Below are results from your test:'
        };
      case 'AllResults':
        return {
          title: 'Your Results',
          description: 'Below are results from your assessment:'
        };
      default:
        return {
          title: '',
          description: ''
        };
    }
  }

  renderCurrentState(state: DerivedState) {
    switch (state.type) {
      case 'EmptyAMH':
      case 'AMHProspect':
        const renderSubtitle = () => {
          if (state.type === 'AMHProspect') {
            return state.amh.units;
          }
          return (
            <div>
              <div>Results</div>
              <div>Pending</div>
            </div>
          );
        };

        const value = () => {
          if (state.type === 'AMHProspect') {
            return state.amh.value;
          }
          return '';
        };

        return (
          <React.Fragment>
            <Result title={'AMH level'} value={value()} subtitle={renderSubtitle()} />
            <AMHContainer>
              AMH is one of many factors in assessing your fertility to help you plan for the
              future.
            </AMHContainer>
            <AMHContainer>
              Schedule an assessment with a fertility specialist to start the conversation today.
            </AMHContainer>
          </React.Fragment>
        );
      case 'AllResults':
        const amh: ResultType = state.amh;
        const afc: ResultType = state.afc;
        return (
          <React.Fragment>
            <Result title={'AMH level'} value={amh.value} subtitle={amh.units} />
            <Result
              title={'Antral follicle count'}
              value={afc.value ? afc.value : 'Unknown'}
              subtitle={afc.units}
            />
          </React.Fragment>
        );
      default:
        return null;
    }
  }

  renderButton(derivedState: DerivedState) {
    const buttonProps = this.getButtonProps(derivedState);
    if (buttonProps) {
      return (
        <ButtonSection>
          <Button
            label={buttonProps.title}
            color={themes.colors.KBYellow}
            onClick={buttonProps.action}
          />
        </ButtonSection>
      );
    }
  }

  render() {
    return (
      <Query query={ResultsQuery}>
        {({ error, loading, data }: ResultsQueryProps) => {
          if (error || loading || !data || !data.results) return null;

          const results: LoadedResults = data.results;

          const derivedState = this.getDerivedState(results);
          if (derivedState.type === 'none') {
            return null;
          }
          const textProps = this.getTitleAttributes(derivedState);

          return (
            <DashboardWidget>
              <Container>
                {this.renderCommentModal()}
                <TitleContainer>
                  <TitleText>{textProps.title}</TitleText>
                  <Description>{textProps.description}</Description>
                </TitleContainer>
                <InfoArea>
                  <InfoAreaContent>{this.renderCurrentState(derivedState)}</InfoAreaContent>
                </InfoArea>
                {this.renderButton(derivedState)}
              </Container>
            </DashboardWidget>
          );
        }}
      </Query>
    );
  }
}

const Container = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  background-color: ${themes.colors.KBNight};
  box-shadow: 0 2px 8px 0 #d8d8d8;
`;

const TitleContainer = styled.div`
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  margin-top: 20px;
`;

const TitleText = styled.div`
  text-align: left;
  ${themes.fonts.Pitch};
  font-size: 18px;
  color: ${themes.colors.KBYellow};
  padding: 0px 0px 8px 20px;
`;

const Description = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  font-weight: 300;
  line-height: 1.43;
  text-align: left;
  color: white;
  padding: 0px 0px 28px 20px;
`;

const InfoArea = styled.div`
  width: 100%;
  min-height: 96px;
`;

const InfoAreaContent = styled.div`
  margin: 12px 24px 12px 24px;
`;

const ButtonSection = styled.div`
  display: flex;
  height: 100px;
  align-items: center;
  justify-content: center;
`;

const AMHContainer = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  font-weight: 300;
  line-height: 1.43;
  text-align: center;
  color: white;
  margin-bottom: 10px;
`;
